import { takeEvery, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  getCurrentSoldOutItemSuccess,
  getInStockItems,
  getInStockItemsSuccess,
  getSoldOutItems,
  getSoldOutItemsSuccess,
  prepareItems,
  refreshItems,
} from "../../slices/ItemStatusSlice";
import { flatItemsFromCategories } from "../../../helpers/catalogueHelpers";
import { sendDataToApp } from "../../../utils/nativeHooks";
import { REACT_NATIVE_APP_EVENTS } from "../../../data/constants";
import { prepareItemsFun, refreshItemsFun } from "./shared";

export * from "./shared";

/**
 * WEB half of `redux-store/saga/itemStatus`. `shared.ts` owns the two identical workers.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 */

/**
 * Fetch in-stock items.
 *
 * ⚠️ **Divergent by ARITY, which is invisible at a glance.** Web passes a THIRD argument,
 * `orderSource`; the other platform's call site passes only two. The endpoint builder takes the
 * extra argument on this platform, so the query string differs — see `index.native.ts`.
 */
function* getInStockItemsData(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.ITEMSTATUS.getAllInStockItems(
        action.payload?.branchId,
        action.payload?.itemType,
        action.payload?.orderSource,
      ),
    });
    yield put(getInStockItemsSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

/**
 * Fetch sold-out items.
 *
 * 🔴 **Fully divergent from the other platform, on four axes at once** — this is the worker that
 * kept the two apps from sharing anything here:
 *  1. it ALWAYS dispatches the success action, with the RAW response; the other platform
 *     dispatches a FLATTENED id list and only sometimes;
 *  2. it pushes the flattened list to the host shell via the native bridge — the other platform
 *     does not, and has no such call;
 *  3. its extra out-of-stock dispatch is gated on a flag carried IN THE ACTION PAYLOAD, whereas
 *     the other platform's single dispatch is gated on a helper that reads business settings
 *     from the store;
 *  4. it dispatches a second, additional action for the current sold-out list, which has no
 *     counterpart on the other platform.
 *
 * ⚠️ Note `sendDataToApp` is called DIRECTLY, not `yield`ed — it is a plain function, not an
 * effect, so it runs synchronously inside the worker. Preserved as written.
 */
function* getSoldOutItemsData(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.ITEMSTATUS.getAllSoldOutItems(),
    });
    yield put(getSoldOutItemsSuccess(response));
    sendDataToApp({
      eventName: REACT_NATIVE_APP_EVENTS.soldOutItemDetail,
      data: flatItemsFromCategories(response),
    });
    if (action?.payload?.isDisplayOutOfStockItem) {
      let soldOutItemsList = flatItemsFromCategories(response);
      yield put(getCurrentSoldOutItemSuccess(soldOutItemsList));
    }
  } catch (error) {
    console.log("error", error);
  }
}

export default function* root(): Generator<any, void, any> {
  yield takeEvery(getInStockItems.type, getInStockItemsData);
  yield takeEvery(getSoldOutItems.type, getSoldOutItemsData);
  yield takeEvery(prepareItems.type, prepareItemsFun);
  yield takeEvery(refreshItems.type, refreshItemsFun);
}
